/* eslint-disable sort-keys */
import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';

import assignmentManager from '../managers/AssignmentManager';
import Auth from '../managers/AuthManager';
import classroomManager from '../managers/ClassroomManager';
import courseManager from '../managers/CourseManager';
import ltiManager from '../managers/LtiManager';
import userManager from '../managers/UserManager';

import { MAX_THRESHOLD_PAGE_SIZE } from '../constants';

import InitService from './InitService';
import UserService from './UserService';

export default class LtiService {
  // ============================================================================
  // LTI INITIALIZATION
  // ============================================================================

  /**
   * Initialize LTI main view context from URL parameters
   *
   * Parse and store LTI parameters (jwt, classroomId, mode), authenticate user,
   * and set active role. Called when backend redirects to /lti-courses.
   *
   * IMPORTANT: Current LTI implementation assumes users will have 'teacher_user' permission.
   * Users without this permission may experience unexpected behavior as most LTI features
   * (content picker, reports, gradebook, etc.) are currently designed for teacher workflows.
   *
   * @param {URLSearchParams} urlParams - URL search parameters
   * @returns {Promise<void>}
   */
  static async initLtiMainViewContext(urlParams) {
    try {
      // Initialize general data (handles dynamic satellite, user data, etc.)
      await InitService.initData(urlParams);

      // Set LTI flags
      assignmentManager.isLtiLaunched = true;
      ltiManager.setIsLti(true);

      // Check for JWT token (from URL parameter)
      const jwtToken = urlParams.get('jwt');
      if (jwtToken) {
        ltiManager.setIsLti13(true);
        ltiManager.setLtiJwtToken(jwtToken);
      }

      // Set classroom context (from URL)
      const classroomId = urlParams.get('classroomId');
      if (classroomId) {
        ltiManager.setLtiClassroomId(classroomId);
        classroomManager.setCurrentClassroomId(classroomId);
      }

      // Set picker mode from URL parameter
      const mode = urlParams.get('mode');
      if (mode) {
        ltiManager.setLtiPickerMode(mode); // 'content' | 'assignment'
      } else {
        ltiManager.setLtiPickerMode('assignment'); // Default when mode not specified
      }

      // Authenticate with authKey if not already logged in (InitService.initData() loads user data)
      if (!Auth.loggedIn()) {
        if (!urlParams.has('authKey') || urlParams.get('authKey') === 'null') {
          ltiManager.setIsLtiUnauthorized(true);
          ltiManager.setIsLtiLoading(false);
          return;
        }

        const authKey = urlParams.get('authKey');
        const loginSuccess = await userManager.authKeyLogIn(authKey);
        if (!loginSuccess) {
          ltiManager.setIsLtiUnauthorized(true);
          ltiManager.setIsLtiLoading(false);
          return;
        }
      }

      // Check if user has teacher permission
      if (!userManager.hasTeacherPermission) {
        ltiManager.setIsLtiUnauthorized(true);
        ltiManager.setIsLtiLoading(false);
        return;
      }

      // If user has multiple roles and has the teacher_user role, set the active role to teacher_user
      if (!userManager.activePermissionId) {
        const roles = UserService.getAvailableRoles();
        if (roles.includes('teacher_user')) {
          userManager.setActivePermissionId('teacher_user');
        }
      }

      // Mark as loaded
      ltiManager.setIsLtiLoading(false);
    } catch (error) {
      console.error('LTI: Error during initialization:', error);
      ltiManager.setIsLtiUnauthorized(true);
      ltiManager.setIsLtiLoading(false);
    }
  }

  // ============================================================================
  // LTI ASSIGNMENT MODE (LTI 1.0/1.1)
  // ============================================================================

  /**
   * Add single LTI assignment (Legacy LTI 1.0/1.1 - Assignment Mode)
   * @param {string} contentItemId - Content item ID
   * @param {string} courseResourceElementId - Element ID
   * @param {string} classroomId - Classroom ID
   * @param {string} courseContentItemId - Course ID
   * @param {boolean} studentReview - Allow student review
   * @returns {Promise} Assignment creation result
   */
  static addLtiAssignment = async (contentItemId, courseResourceElementId, classroomId, courseContentItemId, studentReview) => {
    const activityContent = [{ contentItemId, courseResourceElementId }];
    const activityContentStr = JSON.stringify(activityContent);

    const startDate = new Date(Date.now());
    const endDate = new Date(Date.now());
    endDate.setFullYear(endDate.getFullYear() + 1);

    const result = await assignmentManager.createClassIndividualAssignments({
      activityContentStr,
      additionalPropertiesJson: {
        allowLateSubmission: false,
        ignoreItemOptionShuffle: false,
        showCorrectAnswers: true,
        shuffle_items: true
      },
      classroomId,
      courseContentItemId,
      endRealDate: endDate.toISOString(),
      includeInReports: true,
      instruction: '',
      nickname: '',
      scoresReleased: true,
      startRealDate: startDate.toISOString(),
      studentReview
    });

    return result;
  }

  // ============================================================================
  // LTI CONTENT PICKER DATA LOADING
  // ============================================================================

  /**
   * Load courses for LTI content picker table
   * Fetch top-level courses and populate LTI table data in manager
   * @param {string} classroomId - Classroom ID
   * @returns {Promise<void>}
   */
  static loadContentPickerCourses = async (classroomId) => {
    const page = 0;
    const pageSize = MAX_THRESHOLD_PAGE_SIZE;
    const searchText = '';
    const sortColumn = 'sortName';
    const sortDirection = 'asc';

    await courseManager.fetchUserCourseList(
      classroomId, page, pageSize, searchText, sortColumn, sortDirection
    );

    const courseMap = courseManager.productCourses;
    const courseIds = [];

    courseMap.forEach((course) => {
      const courseId = course.id;

      ltiManager.setLtiTableRowCourse(courseId, {
        id: courseId,
        depth: 0,
        childrenIds: null,
        isExpandable: true,
        name: course.name,
        description: course.description,
        sortName: course.sortName
      });

      courseIds.push(courseId);
    });

    ltiManager.setLtiTableRowCourseIds(courseIds);
  }

  /**
   * Load children for LTI content picker table row
   * Fetch children for a parent row and populate in flat Map, then toggle expanded state
   * @param {string} parentId - Parent row ID
   * @param {string} classroomId - Classroom ID
   * @returns {Promise<void>}
   */
  static loadLtiContentPickerChildren = async (parentId, classroomId) => {
    const parent = ltiManager.ltiTableRowMap.get(parentId);

    if (!parent) {
      console.error('Row not found:', parentId);
      return;
    }

    if (parent.childrenIds !== null) {
      ltiManager.toggleLtiTableRowExpanded(parentId);
    } else {
      const isCourse = parent.depth === 0;
      const courseId = isCourse ? parentId : parent.courseId;

      const children = await courseManager.fetchTeacherCourseElements({
        classroomId,
        courseContentItemId: courseId,
        courseResourceElementId: isCourse ? undefined : parentId,
        includeAlignments: false
      });

      const childrenIds = [];

      children.forEach((child) => {
        const childId = child.elementId;

        ltiManager.setLtiTableRowChild(childId, {
          id: childId,
          courseId,
          depth: parent.depth + 1,
          childrenIds: null,
          isExpandable: !child.deliveryMode,
          name: child.name,
          description: child.description,
          contentItemDescription: child.contentItemDescription,
          deliveryMode: child.deliveryMode,
          autoScored: child.autoScored,
          contentItemId: child.contentItemId,
          entityId: child.entityId,
          courseContentItemId: courseId,
          type: child.type,
          entityTypeId: child.entityTypeId,
          elementId: child.elementId
        });

        childrenIds.push(childId);
      });

      ltiManager.updateLtiTableRowChildrenIds(parentId, childrenIds);
      ltiManager.toggleLtiTableRowExpanded(parentId);
    }
  }

  // ============================================================================
  // LTI CONTENT PICKER MODE (LTI 1.3)
  // ============================================================================

  /**
   * Submit content picker selections (LTI 1.3 Deep Linking - Content Mode)
   * @param {string} classroomId - Classroom ID
   * @param {Array} selectedItems - Selected items from collectContentPickerSelections
   * @param {string} jwt - JWT token
   * @returns {Promise<Object>} { success, deeplinkUrl, jwt, error }
   */
  static submitContentPickerSelections = async (classroomId, selectedItems, jwt) => {
    const data = {
      classroomId,
      courseElements: selectedItems
    };
    const response = await ltiManager.addCourseElementsToLtiCourse(data, jwt);

    if (response && response.deeplinkUrl && response.jwt) {
      return {
        deeplinkUrl: response.deeplinkUrl,
        jwt: response.jwt,
        success: true
      };
    }

    return { error: 'Invalid response from server', success: false };
  }

  /**
   * Validate content picker selections before submission
   * @param {Array} selectedItems - Selected items
   * @returns {Object} { valid: boolean, errors: string[] }
   */
  static validateContentPickerSelections = (selectedItems) => {
    const errors = [];

    if (selectedItems.length === 0) {
      errors.push('No content is selected so nothing has been added.');
    }

    return { errors, valid: errors.length === 0 };
  }

  // ============================================================================
  // LTI CONTENT PICKER HELPERS
  // ============================================================================

  /**
   * Get translated delivery mode text for LTI content picker
   * @param {string} deliveryMode - Delivery mode value
   * @param {Function} t - Translation function
   * @returns {string} Translated delivery mode text
   */
  static getLtiDeliveryModeText = (deliveryMode, t) => {
    switch (deliveryMode) {
      case 'assignable':
        return t('deliveryModeAssignable');
      case 'student_always':
        return t('deliveryModeStudentAlways');
      case 'student_always_assignable':
        return t('deliveryModeStudentAlwaysAssignable');
      case 'student_always_rou_assigned':
        return t('deliveryModeReadOnlyUntilAssigned');
      case 'teacher_only':
        return t('deliveryModeTeacherOnly');
      default:
        return '';
    }
  }

  /**
   * Check if course element is downloadable content for LTI preview
   * @param {Object} courseElement - Course element data
   * @returns {boolean} True if downloadable
   */
  static isLtiDownloadableContent = (courseElement) => {
    const { type, entityTypeId } = courseElement;
    const contentType = type || entityTypeId;

    return contentType === CONTENT_ITEM_TYPES.FILE_RESOURCE ||
           contentType === CONTENT_ITEM_TYPES.AUDIO_RESOURCE ||
           contentType === CONTENT_ITEM_TYPES.VIDEO_RESOURCE ||
           contentType === CONTENT_ITEM_TYPES.IMAGE_RESOURCE;
  }
}
